import React, { useState, useEffect, useContext } from 'react';
import { Alert } from 'antd';
import Spin from 'src/components/common/Spin';
import useFetchReducer from 'src/hooks/useFetchReducer';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import { QaError } from '../types/QaErrors';
import { ColumnSorting } from './QaErrorsTable/QaErrorsTable.config';
import QaErrorsTable from './QaErrorsTable/QaErrorsTable';
import { BugsModal } from '../modals/BugsModal';
import useQaErrorsListFetch from '../hooks/useQaErrorsListFetch';
import { getScriptState } from '../services/qaErrors.service';
import { QaErrorsContext } from '../QaErrorsProvider';
import ScriptActivationModal, { ScriptStateEnhanced } from '../modals/ScriptActivationModal';
import UpdateBtn from './UpdateBtn';
import styles from './QaErrorsList.module.less';
import useQaErrorsTimestampFetch from '../hooks/useQaErrorsTimestampFetch';

const PAGE_NUMBER = 1;
const PAGE_SIZE = 10;

type QaErrorsListProps = {
  scriptActivationEnabled: boolean | null;
};

const QaErrorsList: React.FC<QaErrorsListProps> = ({ scriptActivationEnabled }) => {
  const { sortingcolumn = 'priority', sortingorder = 'ascend' } = useQueryUrlParams();
  const { filters } = useContext(QaErrorsContext);
  const [page, setPage] = useState(PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sorting, setSorting] = useState<ColumnSorting>({ field: sortingcolumn, order: sortingorder });
  const [scriptData, setScriptData] = useState<QaError | null>(null);
  const [scriptDataProps, setScriptDataProps] = useState<QaError | null>(null);
  const [scriptState, fetchScriptState, resetScriptState] = useFetchReducer<ScriptStateEnhanced>(null, []);

  const [{ data, loading, error, total }, reloadList] = useQaErrorsListFetch({
    filters,
    sorting,
    page,
    pageSize,
  });
  const [timestamp, reloadTimestamp] = useQaErrorsTimestampFetch();

  const firstLoading = loading && data === null;

  const showScriptActivationModal = (row: QaError) => {
    const { applicationId: appId, siteCode } = row;
    fetchScriptState(async () => ({
      ...(await getScriptState({ appId, siteCode })),
      scriptName: row.scriptName,
    }));
    setScriptDataProps(row);
  };

  useEffect(() => {
    setPage(PAGE_NUMBER);
    setPageSize(PAGE_SIZE);
  }, [filters]);

  return (
    <>
      {(error || scriptState.error || timestamp.error) && (
        <Alert message="Error" description="Something went wrong. Please try again later..." type="error" showIcon />
      )}
      {scriptData && (
        <BugsModal scriptData={scriptData} isModalOpen={!!scriptData} hideModal={() => setScriptData(null)} />
      )}
      {scriptState.data && (
        <ScriptActivationModal scriptDataProps = {scriptDataProps} data={scriptState.data} afterClose={resetScriptState} onSuccessSubmit={reloadList} />
      )}
      {firstLoading ? (
        <div className={styles.listLoader}>
          <Spin spinning={firstLoading} />
        </div>
      ) : (
        <>
          <UpdateBtn
            timestamp={timestamp.data}
            loading={timestamp.loading}
            onClick={() => {
              reloadList();
              reloadTimestamp();
            }}
          />
          <QaErrorsTable
            items={data || []}
            sorting={sorting}
            loading={loading || scriptState.loading}
            page={page}
            pageSize={pageSize}
            total={total}
            onSortingChange={setSorting}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onShowBugsModal={setScriptData}
            onStatusIconClick={scriptActivationEnabled ? showScriptActivationModal : null}
            reloadList={reloadList}
            filters={filters}
          />
        </>
      )}
    </>
  );
};

export default QaErrorsList;
